import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

// ── Types ──

export type InvestigationType = 'person' | 'domain' | 'email' | 'ip' | 'username' | 'crypto_address' | 'phone';

export interface InvestigationRequest {
  type: InvestigationType;
  target: string;
  depth?: 'shallow' | 'standard' | 'deep';
  modules?: string[];
}

export interface InvestigationResult {
  id: string;
  type: InvestigationType;
  target: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  findings: {
    domain_info?: DomainIntel;
    social_profiles?: SocialProfile[];
    email_breaches?: EmailBreach[];
    network_intel?: NetworkIntel;
    crypto_intel?: CryptoIntel;
    person_info?: PersonIntel;
    timeline: TimelineFinding[];
    related_entities: RelatedEntity[];
  } | null;
  error?: string;
  created_at: string;
  completed_at: string | null;
  sources_count?: number;
  sources_queried?: string[];
}

export interface DomainIntel {
  domain: string;
  registrar: string;
  creation_date: string;
  expiration_date: string;
  nameservers: string[];
  dns_records: Array<{ type: string; value: string }>;
  whois_data: Record<string, string>;
  ssl_info?: { issuer: string; valid_from: string; valid_to: string; subject: string };
  technologies?: string[];
  subdomains?: string[];
}

export interface SocialProfile {
  platform: string;
  username: string;
  url: string;
  bio?: string;
  followers?: number;
  verified?: boolean;
  last_active?: string;
}

export interface EmailBreach {
  breach_name: string;
  breach_date: string;
  data_types: string[];
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
}

export interface NetworkIntel {
  ip: string;
  asn: string;
  isp: string;
  country: string;
  city?: string;
  open_ports?: Array<{ port: number; service: string; version?: string }>;
  reputation_score?: number;
  blacklisted?: boolean;
  related_domains?: string[];
}

export interface CryptoIntel {
  address: string;
  chain: string;
  balance?: string;
  transaction_count?: number;
  first_seen?: string;
  last_seen?: string;
  risk_score?: number;
  tags?: string[];
  related_addresses?: string[];
}

export interface PersonIntel {
  name?: string;
  possible_emails?: string[];
  possible_phones?: string[];
  locations?: string[];
  employers?: string[];
  education?: string[];
  social_profiles?: SocialProfile[];
}

export interface TimelineFinding {
  date: string;
  type: string;
  description: string;
  source: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface RelatedEntity {
  name: string;
  type: string;
  relationship: string;
  confidence: number;
}

// ── API Functions ──

async function investigate(request: InvestigationRequest): Promise<InvestigationResult> {
  const { data } = await apiClient.post('/osint/investigate', request);
  return data;
}

async function fetchInvestigationResults(id: string): Promise<InvestigationResult> {
  const { data } = await apiClient.get(`/osint/investigations/${id}`);
  return data;
}

async function fetchInvestigationHistory(params?: {
  page?: number;
  page_size?: number;
  type?: InvestigationType;
}): Promise<{ items: InvestigationResult[]; total: number }> {
  const { data } = await apiClient.get('/osint/investigations', { params });
  return data;
}

async function fetchDomainIntel(domain: string): Promise<DomainIntel> {
  const { data } = await apiClient.get('/osint/domain', { params: { domain } });
  return data;
}

async function fetchEmailIntel(email: string): Promise<{
  breaches: EmailBreach[];
  social_profiles: SocialProfile[];
  valid: boolean;
  disposable: boolean;
}> {
  const { data } = await apiClient.get('/osint/email', { params: { email } });
  return data;
}

async function addToGraph(entities: RelatedEntity[]): Promise<{ added: number }> {
  const { data } = await apiClient.post('/osint/add-to-graph', { entities });
  return data;
}

// ── Multi-Engine Search & Auto-Pipeline ──

export interface MultiSearchResult {
  query: string;
  total: number;
  engines_used: string[];
  results_by_engine: Record<string, number>;
  results: Array<{ url: string; title: string; snippet: string; source: string }>;
  extracted_entities: Array<{ text: string; type: string; confidence: number }>;
}

export interface AutoPipelineResult {
  pipeline_id: string;
  status: string;
  target: string;
  target_type: string;
  steps: Array<{ step: string; status: string; [key: string]: unknown }>;
  summary: {
    web_results: number;
    entities_discovered: number;
    graph_entities_added: number;
    alerts_generated: number;
    processing_time_ms: number;
  };
  search_results: Array<Record<string, unknown>>;
  entities: Array<Record<string, unknown>>;
  investigation: Record<string, unknown>;
  alerts: Array<Record<string, unknown>>;
}

async function multiEngineSearch(body: {
  query: string;
  engines?: string[];
  num_results?: number;
  extract_entities?: boolean;
}): Promise<MultiSearchResult> {
  const { data } = await apiClient.post('/osint/multi-search', body);
  return data;
}

async function autoPipeline(body: {
  target: string;
  target_type?: string;
  depth?: string;
  auto_add_to_graph?: boolean;
  auto_alert_on_risk?: boolean;
  search_engines?: string[];
  social_platforms?: boolean;
}): Promise<AutoPipelineResult> {
  const { data } = await apiClient.post('/osint/auto-pipeline', body);
  return data;
}

// ── React Query Hooks ──

export function useInvestigate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: investigate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['osint-investigations'] });
    },
  });
}

export function useInvestigationResults(id: string | null) {
  return useQuery({
    queryKey: ['osint-investigation', id],
    queryFn: () => fetchInvestigationResults(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'pending' || data.status === 'running')) {
        return 3000;
      }
      return false;
    },
  });
}

export function useInvestigationHistory(params?: {
  page?: number;
  page_size?: number;
  type?: InvestigationType;
}) {
  return useQuery({
    queryKey: ['osint-investigations', params],
    queryFn: () => fetchInvestigationHistory(params),
    staleTime: 30_000,
  });
}

export function useDomainIntel(domain: string | null) {
  return useQuery({
    queryKey: ['osint-domain', domain],
    queryFn: () => fetchDomainIntel(domain!),
    enabled: !!domain,
    staleTime: 300_000,
  });
}

export function useEmailIntel(email: string | null) {
  return useQuery({
    queryKey: ['osint-email', email],
    queryFn: () => fetchEmailIntel(email!),
    enabled: !!email,
    staleTime: 300_000,
  });
}

export function useAddToGraph() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addToGraph,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['graph'] });
    },
  });
}

export function useMultiEngineSearch() {
  return useMutation({ mutationFn: multiEngineSearch });
}

export function useAutoPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: autoPipeline,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['graph'] });
      queryClient.invalidateQueries({ queryKey: ['osint-investigations'] });
    },
  });
}
